import { FC, forwardRef, ForwardRefRenderFunction, HTMLAttributes, ReactNode } from 'react';
import { twJoin, twMerge } from '@tuic/tailwind-config';
import { Icon, IconName } from '../../../core-components';
import { Image, ImageProps } from '../Image';
import { Skeleton } from '../../../core-components';
import { Pictogram, PictogramName } from '../../../core-components';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { Divider } from '../../../core-components';
import { Badge } from '../Badge';
export type DashboardCardProps = HTMLAttributes<HTMLDivElement> & {
  //TODO: Rename to headline
  title?: string;
  className?: string;
  fallback?: boolean;
  flexMobile?: boolean;
  flexTabletAndDesktop?: boolean;
  tag?: ReactNode;
  secondTag?: ReactNode;
  asChild?: boolean;
  image?: ImageProps;
  icon?: IconName;
  pictogram?: PictogramName;
  badge?: number;
};
const DashboardCardRenderFunction: ForwardRefRenderFunction<HTMLDivElement, DashboardCardProps> = (props, ref) => {
  const {
    className,
    image,
    icon,
    pictogram,
    fallback,
    title,
    flexMobile,
    flexTabletAndDesktop,
    tag,
    secondTag,
    asChild,
    children,
    badge,
    ...rest
  } = props;
  const Component = asChild ? Slot : 'div';
  const showDivider = !props.image && !fallback;
  const cardAspect = twJoin(flexMobile ? 'aspect-[2/1]' : 'aspect-1/1',
  // Considering Grid Gap in the Aspect Ratio here, that's why aspect-[2000/976]
  flexTabletAndDesktop ? 'md:aspect-[2000/976]' : 'md:aspect-1/1');
  return <Component {...rest} className={twMerge('group/dashboard-card flex size-full shrink-0 flex-col rounded-sm overflow-hidden', 'bg-white text-marine-high-emphasis', 'focus:outline-none focus-visible:shadow-focus-state', 'interactive:hover:ring-2 interactive:hover:ring-surface-secondary-20', className)} ref={ref} data-sentry-element="Component" data-sentry-component="DashboardCardRenderFunction" data-sentry-source-file="DashboardCard.tsx">
      <div className="relative w-full overflow-hidden">
        {fallback ? <Skeleton className={twJoin('w-full rounded-none', cardAspect)} /> : <CardImage cardAspect={cardAspect} image={image} icon={icon} pictogram={pictogram} />}
        {!fallback && tag && <div className="absolute bottom-0 right-0 m-2">{tag}</div>}
        {!fallback && secondTag && <div className="absolute right-0 top-0 m-2">{secondTag}</div>}
      </div>
      {showDivider && <div className="px-3">
          <Divider align="horizontal" color="secondary-20" />
        </div>}
      <div className="flex w-full flex-col justify-center py-4 px-2 my-auto">
        {fallback && <Skeleton className="mx-auto my-1 h-3 w-3/4" />}

        {!fallback && title && <div className="line-clamp-3 text-center font-semibold leading-[1.2]">
            {title}
            {badge && <div className="inline-block ml-2">
                <Badge>{badge}</Badge>
              </div>}
          </div>}
      </div>
      <Slottable data-sentry-element="Slottable" data-sentry-source-file="DashboardCard.tsx">{children}</Slottable>
    </Component>;
};
const CardImage: FC<{
  image?: ImageProps;
  icon?: IconName;
  pictogram?: PictogramName;
  cardAspect?: string;
}> = ({
  image,
  icon,
  pictogram,
  cardAspect
}) => {
  if (image) return <div className={twJoin('overflow-hidden w-full', cardAspect)}>
        <Image {...image} className="transition group-interactive-hover/dashboard-card:scale-110 size-full" />
      </div>;
  if (typeof icon === 'undefined' && typeof pictogram === 'undefined') {
    return null;
  }
  return <div className={twJoin('flex h-full flex-col items-center justify-center bg-tertiary', cardAspect)} data-sentry-component="CardImage" data-sentry-source-file="DashboardCard.tsx">
      {icon && <Icon name={icon} className="group-interactive-hover/dashboard-card:scale-110 size-[60px] transition" />}

      {pictogram && <Pictogram name={pictogram} size="md" className="group-interactive-hover/dashboard-card:scale-110 transition" />}
    </div>;
};
export const DashboardCard = forwardRef<HTMLDivElement, DashboardCardProps>(DashboardCardRenderFunction);